import { generateProductId, newFlightLeg } from '../../../components/Booking/bookingConstants';

/**
 * Turns a parsed Airtuerk import into Add Booking's wizard state.
 *
 * The canonical leg shape is already field-for-field what `newFlightLeg()`
 * produces, which is why this is a shallow mapping rather than a translation
 * layer — the parsers were written against that shape deliberately. Keep them
 * in step: a field added to one belongs in the other.
 *
 * What is filled: the flight product, its legs, every traveller, the
 * traveller↔product assignments, and per-traveller pricing.
 * What is deliberately NOT filled: the customer (neither feed carries the
 * retail client — only the agency SUNSKY invoices) and anything the review
 * checklist flagged as a blocker, which stays blank so the wizard's own
 * validation asks for it.
 */

const productStatusFor = (parsed) => {
  if (parsed.cancelled) return 'Cancelled';
  if (parsed.eventType === 'TICKETED') return 'Confirmed';
  return 'Pending';
};

/**
 * SPLIT means the two directions were sold as separate tickets, which shows up
 * as a different airline PNR per direction. Getting this wrong puts the ticket
 * numbers on the wrong side of the pricing screen.
 */
const isSplit = (legs) => {
  const pnrOf = (dir) => [...new Set(legs.filter((l) => l.direction === dir && l.pnr).map((l) => l.pnr))];
  const out = pnrOf('Outbound');
  const inb = pnrOf('Inbound');
  if (out.length === 0 || inb.length === 0) return false;
  return out[0] !== inb[0];
};

const firstPnr = (legs, direction) => legs.find((l) => l.direction === direction && l.pnr)?.pnr || '';

const platformLabel = (source) => (source === 'AER' ? 'Airtuerk cockpit (AERTiCKET)' : 'Airtuerk multicheck');

/**
 * Work out which legs are the way out and which the way back.
 *
 * Each multicheck file is parsed on its own and every leg in it comes out
 * "Outbound", because a single file has no way of knowing it is half a trip.
 * Once the records sharing a PNR are merged that is no longer true: 4KCW0E's
 * DUS→ASR and SAW→DUS are plainly out and back. So directions are decided over
 * the combined, date-ordered set — a journey that ends where an earlier one
 * began is the return.
 */
const reassignDirections = (legs) => {
  if (legs.length < 2) return legs;

  // Break the chain wherever a leg does not continue from the previous arrival.
  //
  // Matching airports alone is not enough: a return through the same hub —
  // FRA→DOH out and DOH→FRA back a fortnight later — connects on paper and
  // would be read as one long journey, losing the return. So a connection also
  // has to be soon after; anything later is a new journey.
  const MAX_CONNECTION_HOURS = 24;
  const at = (leg) => {
    const [y, m, d] = String(leg.date || '').split('-').map(Number);
    const [hh, mm] = String(leg.time || '00:00').split(':').map(Number);
    return [y, m, d].some(Number.isNaN) ? null : Date.UTC(y, m - 1, d, hh || 0, mm || 0);
  };

  const journeys = [];
  let current = [legs[0]];
  for (let i = 1; i < legs.length; i++) {
    const prev = legs[i - 1];
    const sameAirport = Boolean(legs[i].departure && prev.arrival === legs[i].departure);
    const from = at(prev);
    const to = at(legs[i]);
    const soon = from === null || to === null || (to - from) / 3600000 <= MAX_CONNECTION_HOURS;

    if (sameAirport && soon) current.push(legs[i]);
    else {
      journeys.push(current);
      current = [legs[i]];
    }
  }
  journeys.push(current);

  // Two journeys where the second lands back at the start is a return trip.
  // Anything else keeps its legs outbound: calling a third journey "inbound"
  // would be a guess, and multi-city is a shape the wizard already handles.
  const origin = journeys[0][0]?.departure;
  const finish = journeys[journeys.length - 1].slice(-1)[0]?.arrival;
  const isReturn = journeys.length === 2 && origin && finish && origin === finish;

  journeys.forEach((journey, i) => {
    journey.forEach((leg) => {
      leg.direction = isReturn && i === 1 ? 'Inbound' : 'Outbound';
    });
  });

  return legs;
};

/** Identity of a flight leg, for spotting the same one arriving twice. */
const legKey = (l) =>
  [l.airlineCode, l.flightNumber, l.date, l.departure, l.arrival].map((v) => String(v || '').toUpperCase()).join('|');

/**
 * Merge the records that share a PNR into one trip.
 *
 * On multicheck one booking reference routinely spans several files, and the
 * first real fetch showed both shapes it takes. Sometimes they are different
 * journeys — 4KCW0E arrived as DUS→ASR on 12 September and SAW→DUS on 19
 * September, an outbound and a return. Sometimes the second file repeats the
 * same flight with its own ticket number and a small amount, which is an extra
 * sold alongside it: 4KMD1H was EUR 201.57 and then EUR 15.00 on the identical
 * SAW→BRU service.
 *
 * The arithmetic confirms they belong together — those amounts sum exactly to
 * what staff recorded as the booking's cost (201.57 + 15.00 = 216.57). So legs
 * are collected across all the records and de-duplicated by flight identity: a
 * genuinely different journey contributes its legs, a repeat contributes only
 * its money.
 */
const mergeRecords = (primary, related = []) => {
  const records = [primary, ...related.map((r) => r.parsed).filter(Boolean)];

  const legs = [];
  const seen = new Set();
  // Which record each leg came from, so a split ticket can take its number from
  // the half of the trip it actually belongs to.
  const legSource = new Map();
  for (const [index, record] of records.entries()) {
    for (const leg of record.flight?.legs || []) {
      const key = legKey(leg);
      if (seen.has(key)) continue;
      seen.add(key);
      legSource.set(key, index);
      legs.push(leg);
    }
  }

  // Travellers are the same people across the records; keep the fullest list.
  const travelers = records.reduce(
    (best, r) => ((r.travelers?.length || 0) > (best?.length || 0) ? r.travelers : best),
    primary.travelers || []
  );

  const sum = (pick) =>
    records.reduce((a, r) => {
      const v = pick(r);
      return v === null || v === undefined ? a : a + v;
    }, 0);

  const anyCost = records.some((r) => r.pricing?.cost !== null && r.pricing?.cost !== undefined);
  const anySell = records.some((r) => r.pricing?.sell !== null && r.pricing?.sell !== undefined);

  return {
    ...primary,
    flight: { ...primary.flight, legs },
    travelers,
    pricing: {
      ...primary.pricing,
      cost: anyCost ? cents(sum((r) => r.pricing?.cost)) : null,
      sell: anySell ? cents(sum((r) => r.pricing?.sell)) : null,
      agencySurcharge: sum((r) => r.pricing?.agencySurcharge) || null,
      nonAgencySurcharge: sum((r) => r.pricing?.nonAgencySurcharge) || null,
      // Per-traveller lines are summed across records so one traveller shows
      // one figure covering their whole trip, not a line per file.
      perTraveler: (primary.pricing?.perTraveler || []).map((p, i) => ({
        ...p,
        fare: cents(sum((r) => r.pricing?.perTraveler?.[i]?.fare)),
        tax: cents(sum((r) => r.pricing?.perTraveler?.[i]?.tax)),
      })),
    },
    mergedFrom: records.length,
    // Kept for ticket assignment; not part of the canonical shape.
    _records: records,
    _legSource: legSource,
  };
};

/**
 * The ticket number that goes in "Master Ticket Number".
 *
 * The wizard treats it as the first of a run: enter it and the rest of the
 * travellers are filled by incrementing (BookingDetailPricing autoFillTickets).
 * The feed happens to agree — 4KCW0E's pair came through as 5648798192723 and
 * ...724 — so the first traveller's ticket is the right seed. Every traveller's
 * real number is written into their pricing row regardless, so nothing depends
 * on the increment being right.
 */
const masterTicketOf = (record) => record?.travelers?.find((t) => t.ticketNumber)?.ticketNumber || '';

/**
 * Which records supplied each direction, as a map of direction → record
 * indices.
 *
 * A record is placed by the leg it contributed: its first leg's identity is
 * looked up in the merged set, and whatever direction that leg was given is the
 * record's direction. This also catches the extra-sold-alongside shape — 4KMD1H
 * arrived as EUR 201.57 and then EUR 15.00 on the identical SAW→BRU service —
 * because the repeat's leg key matches the surviving leg, so its money lands on
 * the same direction rather than falling out.
 */
const directionSourcesOf = (parsed, legs) => {
  const directionByKey = new Map(legs.map((l) => [legKey(l), l.direction]));
  const sources = new Map();
  (parsed?._records || []).forEach((record, index) => {
    for (const leg of record.flight?.legs || []) {
      const direction = directionByKey.get(legKey(leg));
      if (!direction) continue;
      if (!sources.has(direction)) sources.set(direction, new Set());
      sources.get(direction).add(index);
      break; // one leg is enough to place the record
    }
  });
  return sources;
};

/**
 * Real per-direction money, or null when the feed does not state it.
 *
 * Multicheck sends each half of a split as its own file with its own price:
 * 4LC9X5 is EUR 125,89 out (fare 79.82 + tax 46.07) and EUR 349,90 back (fare
 * 349.90, no tax). Those are not two halves of one number and must not be
 * averaged — splitting the merged 475.79 down the middle bills 237.90 each way
 * and puts tax on a leg that carries none.
 *
 * AER states a split as one price with no breakdown, so both directions come
 * from the same record. There is nothing to read per direction there, and this
 * returns null so the caller falls back to halving.
 */
const perDirectionAmounts = (parsed, legs, travelerCount) => {
  const sources = directionSourcesOf(parsed, legs);
  if (sources.size < 2) return null;

  // Shared records mean the amounts are not attributable per direction.
  const seen = new Set();
  for (const indices of sources.values()) {
    for (const i of indices) {
      if (seen.has(i)) return null;
      seen.add(i);
    }
  }

  const out = {};
  for (const [direction, indices] of sources) {
    out[direction] = Array.from({ length: travelerCount }, (_, i) => {
      let fare = 0;
      let tax = 0;
      for (const index of indices) {
        const row = parsed._records?.[index]?.pricing?.perTraveler?.[i];
        fare += row?.fare || 0;
        tax += row?.tax || 0;
      }
      return { fare: cents(fare), tax: cents(tax) };
    });
  }
  return out;
};

/**
 * What each direction cost, when the feed states them separately.
 *
 * A split ticket is two purchases, and on multicheck they are rarely close:
 * 4LC9X5 is EUR 125,89 out and EUR 349,90 back. Showing only the merged 475,79
 * hides that, and whoever prices the booking needs to see which half the money
 * is on. Returns null when the feed gives one figure for the whole trip.
 */
export const directionTotalsOf = (trip) => {
  // Called from a useMemo that runs before the drawer's own "nothing selected"
  // return, so it is asked about nothing on every render of the list behind it.
  if (!trip) return null;
  const legs = trip.flight?.legs || [];
  const amounts = perDirectionAmounts(trip, legs, (trip?.travelers || []).length);
  if (!amounts) return null;
  const totals = {};
  for (const [direction, rows] of Object.entries(amounts)) {
    totals[direction] = cents(rows.reduce((a, r) => a + r.fare + r.tax, 0));
  }
  return totals;
};

/** For a split ticket, the master from whichever record supplied that direction. */
const ticketForDirection = (parsed, legs, direction) => {
  const leg = legs.find((l) => l.direction === direction);
  if (!leg) return '';
  const index = parsed._legSource?.get(legKey(leg));
  return masterTicketOf(parsed._records?.[index] ?? parsed);
};

/**
 * The whole trip behind one queue row: the record plus every sibling sharing
 * its supplier reference, merged and with directions worked out over the
 * combined set.
 *
 * The drawer reads this so it shows what the wizard will actually receive. A
 * multicheck return arrives as two files, each looking one-way on its own, and
 * showing only the opened file left the return leg and half the money out of
 * view.
 *
 * @param {object} imported  the row from GET /booking-imports/:id
 * @returns {object|null} merged canonical booking, directions applied
 */
export const mergedTripOf = (imported) => {
  if (!imported?.parsed) return null;
  const parsed = mergeRecords(imported.parsed, imported.related || []);
  const legs = reassignDirections(
    [...(parsed.flight?.legs || [])].sort((a, b) =>
      `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`)
    )
  );
  return {
    ...parsed,
    flight: { ...parsed.flight, legs, tripType: tripTypeOf(legs) },
  };
};

/** Trip shape over merged legs — a file that looked one-way may be half a return. */
const tripTypeOf = (legs) =>
  legs.some((l) => l.direction === 'Inbound') ? 'RT' : legs.length > 1 ? 'Multi' : 'OW';

/**
 * @param {object} imported  the row from GET /booking-imports/:id, whose
 *                           `related` array carries the other records sharing
 *                           its PNR
 * @returns {object|null} `prefill` for navigate('/bookings/add', { state: { prefill } })
 */
export const importToPrefill = (imported) => {
  if (!imported?.parsed) return null;
  const parsed = mergeRecords(imported.parsed, imported.related || []);

  const legs = reassignDirections(
    (parsed.flight?.legs || [])
      .map((leg) => ({
        ...newFlightLeg(),
        ...leg,
        // newFlightLeg() gives each leg a unique client id; the canonical shape
        // has none, so the spread above must not clobber it.
        id: `leg-${Math.random().toString(36).slice(2, 10)}`,
      }))
      // Merged records arrive in file order, which is not travel order.
      .sort((a, b) => `${a.date} ${a.time}`.localeCompare(`${b.date} ${b.time}`))
  );

  const productId = generateProductId();
  const split = isSplit(legs);
  const dates = legs.map((l) => l.date).filter(Boolean).sort();
  const tripType = legs.some((l) => l.direction === 'Inbound') ? 'RT' : legs.length > 1 ? 'Multi' : 'OW';

  const product = {
    id: productId,
    type: 'Flight',

    // The supplier every Airtuerk flight in SUNSKY already uses — all 26 flight
    // products keyed by hand in the first three weeks carry this exact name.
    supplier: 'Airtuerk GMBH',
    supplierRef: parsed.locator || '',

    serviceDate: dates[0] || '',
    serviceDateEnd: dates[dates.length - 1] || '',
    status: productStatusFor(parsed),
    creationDate: new Date().toISOString().slice(0, 10),

    notes: [
      `Imported from ${platformLabel(parsed.source)}.`,
      parsed.locator ? `Supplier ref ${parsed.locator}` : null,
      parsed.externalRef ? `Booking no ${parsed.externalRef}` : null,
      parsed.mergedFrom > 1 ? `Merged from ${parsed.mergedFrom} Airtuerk records` : null,
      imported.fileName ? `File ${imported.fileName}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    supplierNote: '',
    customerRemark: '',

    flightBookingMode: 'flightonly',
    // Derived from the merged legs, not from any single record — a file that
    // looked one-way on its own may be half of a return.
    flightType: tripType,
    flightPricingMode: split ? 'SPLIT' : 'FULL',

    // The AIRLINE PNR, not the Airtuerk locator.
    //
    // This used to seed `parsed.locator`, which is the SUPPLIER reference and is
    // already filled into supplierRef just above. The result was a flight whose
    // Supplier Booking Reference and Airline PNR were identical, and both then
    // tripped the duplicate-reference warning against each other. 25 of the 26
    // imported flights on production carry the locator where the airline PNR
    // should be, while the feed held the real one all along (ORD-000220 stored
    // 4KCV7Y; the airline PNR is K653KG).
    //
    // The split path below already read the real value off the legs. This takes
    // the same route: the feed's own airlinePnr first, then the leg PNR, and
    // blank if the feed truly carries neither. Blank is correct — someone can
    // type the PNR in, but nobody can tell that a locator sitting in this field
    // is wrong.
    airlinePnr: split
      ? ''
      : parsed.flight?.airlinePnr || firstPnr(legs, 'Outbound') || firstPnr(legs, 'Inbound') || '',
    // The feed carries a real ticket number per passenger, so the master field
    // is seeded with the first rather than left for someone to retype.
    ticketNumber: split ? '' : masterTicketOf(parsed),
    outboundPnr: split ? firstPnr(legs, 'Outbound') : '',
    outboundTicket: split ? ticketForDirection(parsed, legs, 'Outbound') : '',
    inboundPnr: split ? firstPnr(legs, 'Inbound') : '',
    inboundTicket: split ? ticketForDirection(parsed, legs, 'Inbound') : '',

    legs,
  };

  const travelers = (parsed.travelers || []).map((t) => ({
    firstName: t.firstName || '',
    lastName: (t.lastName || '').toUpperCase(),
    // Blank rather than guessed. AER sends no date of birth at all, and neither
    // feed states a gender for children — the review checklist has already
    // flagged both, and the wizard will not let the booking through without them.
    dob: t.dateOfBirth || '',
    gender: t.gender || '',
  }));

  // Everyone on the file flew on the file's flight — that is what a PNR means.
  const assignments = travelers.map((_, travelerIndex) => ({ travelerIndex, productId }));

  return {
    bookingType: 'Offline',
    products: [product],
    travelers,
    assignments,
    pricing: buildPricing(parsed, productId, split, travelers.length, split ? perDirectionAmounts(parsed, legs, travelers.length) : null),
    importId: imported.id,
    importSummary: {
      source: parsed.source,
      locator: parsed.locator,
      cost: parsed.pricing?.cost ?? null,
      sell: parsed.pricing?.sell ?? null,
      currency: parsed.currency || 'EUR',
      blockers: (imported.warnings || []).filter((w) => w.severity === 'blocker'),
    },
  };
};

const cents = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Per-traveller pricing rows, keyed the way StepPricing reads them:
 * `${travelerIndex}-${productId}`, or with a direction suffix when the flight
 * was sold as two tickets.
 *
 * The wizard's model (StepPricing.jsx:1462) is cost-first:
 *
 *   cost        = netPrice + tax
 *   clientPrice = netPrice + tax + vatTax + commission + segFee
 *
 * so netPrice is the fare BEFORE tax and everything after tax is what we add on
 * top. That matters: putting a tax-inclusive figure in netPrice and then also
 * filling tax bills the customer for the tax twice, and any clientPrice written
 * here is recomputed from those parts the moment someone edits the row.
 *
 * AER states every part, and they reconcile exactly against the sample file:
 *   fare 98.00 + tax 481.21 = 579.21 cost per head, ×4 = 2316.84 (totalFarePlusTax)
 *   + agency surcharge 240.00 (our margin) + non-agency 8.00 (passed through)
 *   = 2564.84 (total) — the stated sell.
 *
 * Multicheck states the SELLING price only and carries no margin field, so the
 * cost half of that equation is genuinely unknown. Rather than invent it, the
 * rows are left out entirely and the NO_COST_PRICE blocker asks a human — the
 * drawer already shows them the selling figure to work back from. Writing
 * netPrice=0 with the sell in clientPrice would look filled-in and save a
 * booking with no cost and, after the first edit, no sell either.
 */
const buildPricing = (parsed, productId, split, travelerCount, byDirection = null) => {
  const pricing = {};
  const per = parsed.pricing?.perTraveler || [];
  if (per.length === 0 || travelerCount === 0) return pricing;

  // No stated purchase price means the wizard's cost-first model cannot be
  // filled honestly. Multicheck always lands here.
  const costTotal = parsed.pricing?.cost ?? null;
  if (costTotal === null) return pricing;

  const rows = per.slice(0, travelerCount);
  const fareSum = rows.reduce((a, p) => a + (p.fare || 0) + (p.tax || 0), 0);

  // Surcharges are stated once for the booking. The agency surcharge is our
  // margin; the rest are supplier charges we pass on. Both reach the customer,
  // which is why both belong after tax rather than inside netPrice.
  const marginTotal = parsed.pricing?.agencySurcharge ?? 0;
  const passThroughTotal =
    (parsed.pricing?.nonAgencySurcharge ?? 0) +
    (parsed.pricing?.obFees ?? 0) +
    (parsed.pricing?.ancillaries ?? 0);

  const directions = split ? ['Outbound', 'Inbound'] : [null];

  /**
   * A split ticket is two halves of one price, not the price twice — and the
   * halves must add back up to the whole. Rounding each half independently
   * over-bills: 481.21 halves to 240.605, which rounds to 240.61 twice and
   * arrives at 481.22. So the first direction is rounded and the second takes
   * whatever is left.
   */
  const halves = (value) => {
    if (!split) return [cents(value)];
    const first = cents(value / 2);
    return [first, cents(value - first)];
  };

  rows.forEach((row, i) => {
    const gross = (row.fare || 0) + (row.tax || 0);
    const share = fareSum > 0 ? gross / fareSum : 1 / rows.length;

    // Where the feed states each direction's own price, use it. Only fall back
    // to halving the total when it does not.
    const stated = byDirection ? directions.map((dir) => byDirection[dir]?.[i]) : [];
    const useStated = stated.length > 0 && stated.every(Boolean);

    const net = useStated ? stated.map((s) => s.fare) : halves(row.fare || 0);
    const tax = useStated ? stated.map((s) => s.tax) : halves(row.tax || 0);
    const commission = halves(marginTotal * share);
    const segFee = halves(passThroughTotal * share);

    directions.forEach((dir, d) => {
      const key = dir ? `${i}-${productId}-${dir}` : `${i}-${productId}`;
      pricing[key] = {
        netPrice: net[d],
        tax: tax[d],
        vatTax: 0,
        commission: commission[d],
        segFee: segFee[d],
        // Written to match what the wizard would compute, so an untouched row
        // and an edited one show the same number.
        clientPrice: cents(net[d] + tax[d] + commission[d] + segFee[d]),
        ticketNumber: parsed.travelers?.[i]?.ticketNumber || '',
      };
    });
  });

  return pricing;
};

export default importToPrefill;
